/** @jsxImportSource ./simboluri-jsx */
import { CoefRand, GeoDosar, GeoResult, StepState, fmtDate } from "./tipuri";
import { numar, t, useT, useTx } from "../i18n";
import type { Cheie } from "../i18n";
import { fileDownloadUrl } from "./apiStructura";
import { useFileIdByName } from "./useFileIds";
import Info from "./Info";
import Marime, { EtichetaNatura } from "./Marime";
import TextSimboluri from "./Simbol";
import { DescriereaPasului } from "./TitluPas";

// Pasul 1 — Studiu geotehnic: parametrii extrași din PDF (LLM cu vision,
// merge pe scanuri) + calculele Fazei 1.1 (presiunea convențională corectată).

function num(v: number | null | undefined, unit = "", dash = "—") {
  return v == null ? dash : `${v}${unit}`;
}

const nr = (v: number, zecimale: number) => numar(v, {
  minimumFractionDigits: zecimale, maximumFractionDigits: zecimale });

// Tabelul complet al unui coeficient de normativ, cu rândul aplicat marcat:
// cifra folosită se citește în contextul tuturor valorilor posibile, ca să se
// vadă că nu e o valoare „din senin", ci un rând ales de tipul stratului.
function TabelCoef({
  simbol,
  randuri,
  cheie,
  zecimale,
}: {
  simbol: string;
  randuri: CoefRand[];
  cheie?: string;
  zecimale: number;
}) {
  const t = useT();
  return (
    <table className="sg-table st-tabel-coef">
      <thead>
        <tr>
          <th>{t("struct.geo.col_teren")}</th>
          <th>{simbol}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {randuri.map((rd) => (
          <tr key={rd.cheie} className={rd.cheie === cheie ? "sg-tr-activ" : ""}>
            <td>{rd.teren}</td>
            <td className="sg-mono">{nr(rd.valoare, zecimale)}</td>
            <td className="sg-mono">
              {rd.cheie === cheie ? t("struct.geo.rand_aplicat") : ""}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function StepGeo({
  projectId,
  state,
  running,
}: {
  projectId: string;
  state: StepState | null;
  running: boolean;
}) {
  // resolves the old structure-side file names to platform file UUIDs, for
  // the download links (the facade serves files by UUID)
  const t = useT();
  const tx = useTx();
  const fileIdByName = useFileIdByName(projectId);
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.pas.ruleaza")}</strong>
          <div className="sg-muted">{t("struct.geo.ruleaza")}</div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.pas.nerulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="studiu_geotehnic" />
      </div>
    );
  }

  const r = state.result as unknown as GeoResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.pas.esuat", {
            eroare: r?.error ?? t("struct.pas.eroare_necunoscuta"),
          })}
        </div>
        <div className="sg-muted">{t("struct.pas.ruleaza_din_nou")}</div>
      </div>
    );
  }

  const p = r.parametri;
  const f = r.fundare;

  return (
    <div className="st-result">
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.geo.p_conv")}
            <Marime cheie="p_conv" />
          </div>
          <div className="st-card-value">{f.p_conv_kpa} kPa</div>
          <div className="st-card-sub">
            {t("struct.geo.p_conv_sub", {
              p: p.p_conv_kpa, cd: f.cd_kpa, cb: f.cb_kpa,
              df: f.df_m, b: f.b_m,
            })}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.geo.praguri")}
            <Marime cheie="praguri" />
          </div>
          <div className="st-card-value">{f.prag_static_kpa} <span className="st-small">kPa</span></div>
          <div className="st-card-sub">
            {t("struct.geo.praguri_sub", { n: f.prag_dinamic_kpa })}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">{t("struct.geo.seism_inghet")}<Marime cheie="a_g" /><Marime cheie="Tc" /><Marime cheie="adancime_inghet" /><Marime cheie="apa_subterana" /></div>
          <div className="st-card-value">ag {num(p.ag)}</div>
          <div className="st-card-sub">
            {t("struct.geo.seism_sub", {
              tc: num(p.tc_s, " s"),
              inghet: num(p.adancime_inghet_m, " m"),
              apa: num(p.apa_subterana_m, " m"),
            })}
          </div>
        </div>
      </div>

      <Zonare z={(r as any).zonare} />

      <div className="st-findings">
        <h3>{t("struct.geo.stratificatia")}</h3>
        <table className="sg-table st-strat">
          <thead>
            <tr>
              <th>{t("struct.geo.col_strat")}</th>
              <th>{t("struct.geo.col_adancime")}</th>
              <th>γ</th><th>φ</th><th>c</th>
              <th>E</th><th>n</th><th>Ic</th>
              <th>{t("struct.geo.col_tip")}</th>
            </tr>
          </thead>
          <tbody>
            {p.straturi.map((s, i) => (
              <tr key={i}>
                <td>{s.nume}</td>
                <td className="sg-mono">
                  {num(s.adancime_de_la_m)}–{num(s.adancime_pana_la_m)} m
                </td>
                <td className="sg-mono">{num(s.gamma_kn_m3)}</td>
                <td className="sg-mono">{num(s.phi_grade, "°")}</td>
                <td className="sg-mono">{num(s.coeziune_kpa)}</td>
                <td className="sg-mono">{num(s.E_kpa)}</td>
                <td className="sg-mono">{num(s.porozitate_pct, "%")}</td>
                <td className="sg-mono">{num(s.Ic)}</td>
                <td>{s.tip}{s.plasticitate && s.plasticitate !== "null" ? `, ${s.plasticitate}` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sg-muted" style={{ marginTop: 8 }}>
          {t("struct.geo.unitati")}
          <strong>{r.dosar?.provenienta?.straturi ?? r.fisier.name}</strong>
        </div>
      </div>

      {r.dosar && r.dosar.nr_total > 1 && <Dosar d={r.dosar} projectId={projectId} />}

      <div className="st-findings">
        <h3>{t("struct.geo.faza11")}</h3>
        <div className="st-finding">
          <span className="st-finding-label">{t("struct.geo.strat_fundare")}<Marime cheie="strat_fundare" /></span>
          <div>
            {f.strat_fundare ?? "—"}{" "}
            <span className="sg-muted">
              {t("struct.geo.la_df", {
                df: f.df_m,
                strat: f.strat_de_la_m != null
                  ? t("struct.geo.stratul_de_la", {
                      de_la: f.strat_de_la_m,
                      pana_la: num(f.strat_pana_la_m),
                    })
                  : "",
              })}
            </span>
          </div>
        </div>
        <div className="st-finding">
          <span className="st-finding-label">{t("struct.geo.coeficienti")}<Marime cheie="k1" /><Marime cheie="k2" /></span>
          <div>
            <div>
              {f.k1_regula}
              <Marime cheie="k1" />
            </div>
            <div>
              {f.k2_regula}
              <Marime cheie="k2" />
            </div>
          </div>
        </div>
        <div className="st-finding">
          <span className="st-finding-label">{t("struct.geo.de_unde_k")}</span>
          <div>
            <div className="sg-muted" style={{ marginBottom: 8 }}>
              {tx("struct.geo.de_unde_k_text")}
            </div>
            <ol className="st-lant">
              <li>
                {t("struct.geo.lant1")}
                {fileIdByName(r.fisier.name) ? (
                  <a className="sg-link"
                     href={fileDownloadUrl(projectId, fileIdByName(r.fisier.name)!)}>
                    {r.fisier.name}
                  </a>
                ) : (
                  <span className="sg-mono">{r.fisier.name}</span>
                )}{" "}
                {t("struct.geo.lant1b")}
              </li>
              <li>
                {t("struct.geo.lant2", { df: f.df_m })}
                <strong>{f.strat_fundare ?? "?"}</strong>
                {f.strat_de_la_m != null
                  ? t("struct.geo.lant2b", {
                      de_la: f.strat_de_la_m, pana_la: num(f.strat_pana_la_m),
                    })
                  : ""}
                .
              </li>
              <li>
                {t("struct.geo.lant3")}
                <strong>
                  {f.strat_tip ?? t("struct.geo.neclasificat")}
                  {f.strat_plasticitate && f.strat_plasticitate !== "null"
                    ? t("struct.geo.plasticitate", { ce: f.strat_plasticitate })
                    : ""}
                </strong>{" "}
                — {r.coeficienti?.sursa_tip_strat ??
                  t("struct.geo.sursa_tip_implicit")}
                .
              </li>
              <li>
                {t("struct.geo.lant4")}
                <span className="sg-mono">
                  K1 = {nr(f.k1, 2)}, K2 = {nr(f.k2, 1)}
                </span>
                .
              </li>
            </ol>
            {r.coeficienti ? (
              <>
                <div className="st-coef-tabele">
                  <div>
                    <div className="st-coef-titlu">
                      {t("struct.geo.k1_titlu")}
                    </div>
                    <TabelCoef
                      simbol="K1"
                      randuri={r.coeficienti.k1}
                      cheie={f.k1_cheie}
                      zecimale={2}
                    />
                  </div>
                  <div>
                    <div className="st-coef-titlu">
                      {t("struct.geo.k2_titlu")}
                    </div>
                    <TabelCoef
                      simbol="K2"
                      randuri={r.coeficienti.k2}
                      cheie={f.k2_cheie}
                      zecimale={1}
                    />
                  </div>
                </div>
                <div className="sg-muted" style={{ marginTop: 8 }}>
                  {t("struct.geo.sursa_tabele", { sursa: r.coeficienti.sursa })}
                </div>
              </>
            ) : (
              <div className="sg-muted">
                {t("struct.geo.fara_tabele")}
              </div>
            )}
          </div>
        </div>
        <div className="st-finding">
          <span className="st-finding-label">{t("struct.geo.formule")}</span>
          <div className="st-formulas">
            <div className="st-formula">
              <span className="st-formula-what">
                {t("struct.geo.f_pbar", {
                  df: num(f.df_referinta_m, " m", t("struct.geo.df_referinta_implicit")),
                })}
                <Marime cheie="p_bar_conv" />
              </span>
              p̄conv = {p.p_conv_kpa} kPa
              {f.nota_referinta_studiu && (
                <div className="st-warn">⚠ {f.nota_referinta_studiu}</div>
              )}
            </div>
            <div className="st-formula">
              <span className="st-formula-what">
                {t("struct.geo.f_cd", {
                  df: f.df_m, semn: f.df_m < 2 ? "< 2 m" : "≥ 2 m",
                })}
                <Marime cheie="cd" />
              </span>
              {f.df_m < 2 ? (
                <>CD = p̄conv · (Df − 2) / 4 = {p.p_conv_kpa} · ({f.df_m} − 2) / 4 = <strong>{f.cd_kpa} kPa</strong></>
              ) : (
                <>CD = K2 · γ · (Df − 2) = {f.k2} · {f.gamma_kn_m3} · ({f.df_m} − 2) = <strong>{f.cd_kpa} kPa</strong></>
              )}
            </div>
            <div className="st-formula">
              <span className="st-formula-what">
                {t("struct.geo.f_cb", {
                  b: f.b_m, semn: f.b_m < 5 ? "< 5 m" : "≥ 5 m",
                })}
                <Marime cheie="cb" />
              </span>
              {f.b_m < 5 ? (
                <>CB = p̄conv · K1 · (B − 1) = {p.p_conv_kpa} · {f.k1} · ({f.b_m} − 1) = <strong>{f.cb_kpa} kPa</strong></>
              ) : (
                <>CB = 0,2÷0,4 · p̄conv = <strong>{f.cb_kpa} kPa</strong></>
              )}
            </div>
            <div className="st-formula">
              <span className="st-formula-what">
                {t("struct.geo.f_pconv")}
                <Marime cheie="p_conv" />
              </span>
              p_conv = p̄conv + CD + CB = {p.p_conv_kpa} + ({f.cd_kpa}) + ({f.cb_kpa}) = <strong>{f.p_conv_kpa} kPa</strong>
            </div>
            <div className="st-formula">
              <span className="st-formula-what">
                {t("struct.geo.f_static")}
                <Marime cheie="praguri" />
              </span>
              p_verificare,static = 1,10 · p_conv = 1,10 · {f.p_conv_kpa} = <strong>{f.prag_static_kpa} kPa</strong>
            </div>
            <div className="st-formula">
              <span className="st-formula-what">
                {t("struct.geo.f_dinamic")}
                <Marime cheie="praguri" />
              </span>
              p_verificare,dinamic = 1,35 · p_conv = 1,35 · {f.p_conv_kpa} = <strong>{f.prag_dinamic_kpa} kPa</strong>
            </div>
            <div className="st-formula">
              <span className="st-formula-what">
                {t("struct.geo.f_extrase")}
                <Info
                  titlu={t("struct.geo.info_citite_titlu")}
                  ce={t("struct.geo.info_citite_ce")}
                  parametri={[
                    { simbol: "ag", nume: t("struct.geo.p_ag"), sursa: t("struct.geo.p_ag_sursa") },
                    { simbol: "Tc", nume: t("struct.geo.p_tc"), sursa: t("struct.geo.p_ag_sursa") },
                    { simbol: t("struct.geo.p_inghet"), nume: t("struct.geo.p_inghet_nume"), sursa: t("struct.geo.p_inghet_sursa") },
                    { simbol: t("struct.geo.p_apa"), nume: t("struct.geo.p_apa_nume"), sursa: t("struct.geo.p_apa_sursa") },
                  ]}
                />
              </span>
              {t("struct.geo.extrase_text", {
                ag: num(p.ag), tc: num(p.tc_s, " s"),
                inghet: num(p.adancime_inghet_m, " m"),
                apa: num(p.apa_subterana_m, " m"),
              })}
            </div>
          </div>
        </div>
        {p.ks_recomandat_dan_cm3 && (
          <div className="st-finding">
            <span className="st-finding-label">{t("struct.geo.ks")}<EtichetaNatura cheie="ks" /><Marime cheie="ks" /></span>
            <div>
              {p.ks_recomandat_dan_cm3}{" "}
              <span className="sg-muted">
                {t("struct.geo.ks_nota")}
              </span>
            </div>
          </div>
        )}
        {p.observatii && (
          <div className="st-finding">
            <span className="st-finding-label">{t("struct.geo.observatii")}</span>
            <div className="sg-muted">{p.observatii}</div>
          </div>
        )}
        <div className="sg-muted st-ran-at">
          {t("struct.pas.rulat_la", { cand: fmtDate(state.updated_at) })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dosarul geotehnic: pe proiectele complexe studiul e din 10–20 de piese. Se
// arată integral CE s-a citit și CE s-a sărit (cu motivul), de unde vine
// fiecare parametru și ce valori diferite dădeau celelalte documente — un
// document sărit trebuie să se vadă, nu să dispară tăcut.
// ---------------------------------------------------------------------------

const STARE_ETICHETA: Record<string, Cheie> = {
  citit: "struct.dos.stare_citit",
  ignorat: "struct.dos.stare_ignorat",
  necitit: "struct.dos.stare_necitit",
  neacceptat: "struct.dos.stare_neacceptat",
  eroare: "struct.dos.stare_eroare",
};

const ROL_ETICHETA: Record<string, Cheie> = {
  parametri_calcul: "struct.dos.rol_parametri",
  raport_principal: "struct.dos.rol_raport",
  anexa_foraje: "struct.dos.rol_foraje",
  buletin_laborator: "struct.dos.rol_laborator",
  seismicitate: "struct.dos.rol_seismicitate",
};

const CAMP_STRAT_ETICHETA: Record<string, string> = {
  gamma_kn_m3: "γ",
  phi_grade: "φ",
  coeziune_kpa: "c",
  E_kpa: "E",
  porozitate_pct: "n",
  Ic: "I_C",
  tip: "tip",
  plasticitate: "plasticitate",
};


// ZONAREA SEISMICĂ — a_g și T_C vin din HARTĂ, nu din studiu
//.
//
// Pe Bloc Oltenitei P+2 (Sector 4, București) studiul geotehnic a transcris
// T_C = 1,0 s acolo unde zonarea dă 1,6 s, iar din T_C ies plafonul factorului
// `c` și coada spectrului. Cauza nu e o eroare de citire, e o ordine greșită a
// surselor: pentru stratificație sau p_conv studiul e singura sursă — sunt
// măsurători de pe amplasamentul ăsta — dar a_g și T_C sunt valori de hartă,
// aceleași pentru toată localitatea.
//
// Blocul arată AMBELE valori când diferă. O corecție tăcută ar fi la fel de
// proastă ca greșeala: cine deschide studiul și vede 1,0 trebuie să afle de pe
// pagină de ce calculul merge cu 1,6.
function Zonare({ z }: { z: any }) {
  const t = useT();
  if (!z) return null;
  const g = z.gasit;
  return (
    <div className="st-findings">
      <h3>
        {t("struct.zon.titlu")}
        <Info
          titlu={t("struct.zon.info_titlu")}
          ce={t("struct.zon.info_ce")}
          deCe={t("struct.zon.info_de_ce")}
          parametri={[
            { simbol: "a_g", nume: t("struct.zon.p_ag"), sursa: t("struct.zon.p_ag_sursa") },
            { simbol: "T_C", nume: t("struct.zon.p_tc"), sursa: t("struct.zon.p_tc_sursa") },
          ]}
        />
      </h3>
      <table className="sg-table">
        <tbody>
          <tr>
            <td>{t("struct.zon.amplasament")}</td>
            <td colSpan={2}>
              {z.amplasament?.adresa || z.amplasament?.localitate
                || <span className="sg-muted">{t("struct.zon.necunoscut")}</span>}
              {g && (
                <div className="sg-muted" style={{ fontSize: 11 }}>
                  {t("struct.zon.identificat", {
                    localitate: g.localitate, judet: g.judet,
                  })}
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td>a_g</td>
            <td className="sg-mono">{num(z.folosit?.ag)}</td>
            <td className="sg-muted">
              <TextSimboluri text={z.surse?.ag} />
            </td>
          </tr>
          <tr>
            <td>T_C / T_B / T_D</td>
            <td className="sg-mono">
              {num(z.folosit?.tc_s, " s")} / {num(z.folosit?.tb_s, " s")} /{" "}
              {num(z.folosit?.td_s, " s")}
            </td>
            <td className="sg-muted">
              <TextSimboluri text={z.surse?.tc_s} />
            </td>
          </tr>
        </tbody>
      </table>
      {(z.divergente || []).map((d: any) => (
        <p key={d.parametru} className="st-bad" style={{ fontSize: 12,
                                                        maxWidth: 900 }}>
          <TextSimboluri text={d.mesaj} />
        </p>
      ))}
      <p className="sg-muted" style={{ fontSize: 12, maxWidth: 900 }}>
        <TextSimboluri text={z.nota} />
      </p>
    </div>
  );
}
const PARAM_SIMBOL: Record<string, string> = {
  p_conv_kpa: "p̄conv", ag: "a_g", tc_s: "T_C",
};

const PARAM_ETICHETA: Record<string, Cheie> = {
  straturi: "struct.dos.par_straturi",
  p_conv_df_referinta_m: "struct.dos.par_df_ref",
  apa_subterana_m: "struct.dos.par_apa",
  adancime_inghet_m: "struct.dos.par_inghet",
  ks_recomandat_dan_cm3: "struct.dos.par_ks",
  adancime_fundare_recomandata_m: "struct.dos.par_df",
  latime_fundatie_minima_recomandata_m: "struct.dos.par_b",
};

/** numele unui parametru de dosar: cheia de catalog, altfel simbolul, altfel
 *  chiar cheia brută — ca un parametru nou să rămână vizibil. */
function numeParam(k: string): string {
  return PARAM_ETICHETA[k] ? t(PARAM_ETICHETA[k]) : PARAM_SIMBOL[k] ?? k;
}

function Dosar({ d, projectId }: { d: GeoDosar; projectId: string }) {
  const t = useT();
  const fileIdByName = useFileIdByName(projectId);
  const provenienta = Object.entries(d.provenienta);
  return (
    <div className="st-findings">
      <h3>
        {t("struct.dos.titlu", { citite: d.nr_citite, total: d.nr_total })}
        <Info
          titlu={t("struct.dos.info_titlu")}
          ce={t("struct.dos.info_ce")}
          deCe={t("struct.dos.info_de_ce")}
          parametri={[
            { simbol: t("struct.dos.stare_citit"), nume: t("struct.dos.p_citit_nume"), sursa: t("struct.dos.p_citit_sursa") },
            { simbol: t("struct.dos.stare_ignorat"), nume: t("struct.dos.p_sarit_nume"), sursa: t("struct.dos.p_sarit_sursa") },
            { simbol: t("struct.dos.stare_neacceptat"), nume: t("struct.dos.p_format_nume"), sursa: t("struct.dos.p_format_sursa") },
          ]}
        />
      </h3>
      <table className="sg-table">
        <thead>
          <tr>
            <th>{t("struct.dos.col_document")}</th>
            <th>{t("struct.dos.col_rol")}</th>
            <th>{t("struct.dos.col_stare")}</th>
            <th>{t("struct.dos.col_motiv")}</th>
          </tr>
        </thead>
        <tbody>
          {d.documente.map((doc, i) => (
            <tr key={i}>
              <td>
                {fileIdByName(doc.nume) ? (
                  <a className="sg-link"
                     href={fileDownloadUrl(projectId, fileIdByName(doc.nume)!)}>{doc.nume}</a>
                ) : doc.nume}
              </td>
              <td className="sg-muted">
                {ROL_ETICHETA[doc.rol] ? t(ROL_ETICHETA[doc.rol]) : doc.rol}
              </td>
              <td className="sg-mono">
                {STARE_ETICHETA[doc.stare] ? t(STARE_ETICHETA[doc.stare]) : doc.stare}
              </td>
              <td className="sg-muted">{doc.motiv}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {provenienta.length > 0 && (
        <div className="st-finding" style={{ marginTop: 12 }}>
          <span className="st-finding-label">{t("struct.dos.provenienta")}</span>
          <div className="sg-muted">
            {provenienta.map(([k, v]) => `${numeParam(k)}: ${v}`).join(" · ")}
          </div>
        </div>
      )}

      {(d.completari_straturi?.length ?? 0) > 0 && (
        <div className="st-finding">
          <span className="st-finding-label">
            {t("struct.dos.completari")}
          </span>
          <div>
            <div className="sg-muted" style={{ marginBottom: 6 }}>
              {t("struct.dos.completari_text")}
            </div>
            <table className="sg-table">
              <thead>
                <tr>
                  <th>{t("struct.geo.col_strat")}</th>
                  <th>{t("struct.dos.col_de_la")}</th>
                  <th>{t("struct.dos.col_camp")}</th>
                  <th>{t("struct.dos.col_valoare")}</th>
                  <th>{t("struct.dos.col_din")}</th>
                </tr>
              </thead>
              <tbody>
                {d.completari_straturi!.map((c, i) => (
                  <tr key={i}>
                    <td>{c.strat}</td>
                    <td className="sg-mono">
                      {c.adancime_de_la_m != null ? `${c.adancime_de_la_m} m` : "—"}
                    </td>
                    <td>{CAMP_STRAT_ETICHETA[c.camp] ?? c.camp}</td>
                    <td className="sg-mono">{String(c.valoare)}</td>
                    <td className="sg-muted">{c.sursa}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {d.conflicte.length > 0 && (
        <div className="st-finding">
          <span className="st-finding-label">{t("struct.dos.conflicte")}</span>
          <div>
            <div className="sg-muted" style={{ marginBottom: 6 }}>
              {t("struct.dos.conflicte_text")}
            </div>
            <table className="sg-table">
              <thead>
                <tr>
                  <th>{t("struct.dos.col_parametru")}</th>
                  <th>{t("struct.dos.col_folosit")}</th>
                  <th>{t("struct.dos.col_din")}</th>
                  <th>{t("struct.dos.col_alternativ")}</th>
                  <th>{t("struct.dos.col_din")}</th>
                </tr>
              </thead>
              <tbody>
                {d.conflicte.map((c, i) => (
                  <tr key={i}>
                    <td>{numeParam(c.parametru)}</td>
                    <td className="sg-mono">{String(c.folosit)}</td>
                    <td className="sg-muted">{c.sursa_folosita}</td>
                    <td className="sg-mono">{String(c.alternativ)}</td>
                    <td className="sg-muted">{c.sursa_alternativa}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
